import React, { FC, PropsWithChildren, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Separator, SidebarProvider, SidebarTrigger, useSidebar } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { helpSidebars } from '~/components/HelpSidebar';
import { NotificationList } from '~/components/NotificationList';
import { Logo } from '~/elements/Logo';
import { useScrollPosition } from '~/hooks';
import { AppsRedirectModal } from '~/lib/apps/RedirectModal';
import { useNavigate } from '~/lib/navigation';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useScrollStore } from '~/stores/Scroll';
import { useSessionStore } from '~/stores/Session';

import { AppSidebar } from './AppSidebar';
import { BreadcrumbTitleProvider, PageBreadcrumbs } from './PageBreadcrumbs';
import {
  PageFrameActionsSlot,
  PageFrameHeaderDecorationSlot,
  PageFrameProvider,
  useHeaderBorder,
} from './PageFrameContext';
import { EventsButton, ModeSelector, UserDropdown } from './Toolbar';

interface LocationState {
  from?: string;
}

const SIDEBAR_AUTO_COLLAPSE_QUERY = '(max-width: 1024px)';

const SidebarAutoCollapse: FC = () => {
  const { setOpen } = useSidebar();
  const prevBreakpointMatch = useRef<boolean | null>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia(SIDEBAR_AUTO_COLLAPSE_QUERY);

    const handleMatch = (matches: boolean) => {
      // Only auto-adjust when crossing the breakpoint, not on manual toggles
      if (prevBreakpointMatch.current === null) {
        // Initial mount: set based on viewport
        setOpen(!matches);
        prevBreakpointMatch.current = matches;
      } else if (prevBreakpointMatch.current !== matches) {
        // Crossing breakpoint: auto-adjust
        setOpen(!matches);
        prevBreakpointMatch.current = matches;
      }
      // If breakpoint hasn't changed, don't override manual toggles
    };

    // Set initial state based on viewport
    handleMatch(mediaQuery.matches);

    // Listen to resize events to automatically adjust when crossing breakpoint
    const onChange = (event: MediaQueryListEvent) => handleMatch(event.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onChange);
      return () => mediaQuery.removeEventListener('change', onChange);
    }

    mediaQuery.addListener(onChange);
    return () => mediaQuery.removeListener(onChange);
  }, [setOpen]);

  return null;
};

const FadeIn: FC<PropsWithChildren<{ show?: boolean }>> = ({ children, show }) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    if (show !== undefined) {
      setIsVisible(show);
    }
    if (show === undefined) {
      setIsVisible(true);
    }
  }, [show]);
  return (
    <div
      style={{
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 200ms ease',
      }}
    >
      {children}
    </div>
  );
};

const PageFrameHeader: FC<{ scrollY: number }> = ({ scrollY }) => {
  const borderMode = useHeaderBorder();
  const showBorder = borderMode === true || (borderMode === 'onScroll' && scrollY > 0);

  return (
    <header
      data-slot="page-frame-header"
      className={`relative flex min-h-13 shrink-0 items-center flex-wrap gap-x-2 gap-y-3 px-3 py-3 lg:px-6 transition-shadow duration-200 ${showBorder ? 'shadow-border-b' : ''}`}
      style={{ containerType: 'inline-size', containerName: 'header' }}
    >
      <SidebarTrigger className="-ml-1 shadow-none hover:shadow-none dark:shadow-none dark:hover:shadow-none lg:hidden" />
      <Separator orientation="vertical" className="mr-1 lg:hidden data-[orientation=vertical]:h-4" />
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <PageBreadcrumbs />
        <PageFrameHeaderDecorationSlot />
      </div>
      <PageFrameActionsSlot />
    </header>
  );
};

export const Layout: FC<PropsWithChildren> = ({ children }) => {
  const setPageFrameScroll = useScrollStore((s) => s.setPageFrameScroll);
  const { openHelpSidebar } = useHelpSidebarStore();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScrollPosition(wrapperRef);
  const { currentPlatform, currentUser } = useSessionStore();
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as LocationState;
  const shouldFadeIn = useRef((state?.from ?? null) === 'auth');
  const [contentVisible, setContentVisible] = useState(!shouldFadeIn.current);

  useEffect(() => {
    setPageFrameScroll(scrollY);
  }, [scrollY]);

  useLayoutEffect(() => {
    // Flip visibility on next frame so the initial paint is at opacity 0 and
    // the CSS transition has something to animate toward.
    if (!contentVisible) {
      const id = requestAnimationFrame(() => setContentVisible(true));
      return () => cancelAnimationFrame(id);
    }
  }, [contentVisible]);

  useEffect(() => {
    if (state && state.from === 'register') {
      openHelpSidebar('Onboarding');
      navigate({ pathname: location.pathname, search: location.search }, { replace: true });
      return;
    }
    if (state && state.from === 'auth') {
      const t = window.setTimeout(() => {
        navigate({ pathname: location.pathname, search: location.search }, { replace: true });
      }, 400);
      return () => window.clearTimeout(t);
    }
  }, [location]);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) {
      return;
    }

    wrapper.scrollTo({ top: 0 });
  }, [wrapperRef, location]);

  return (
    <PageFrameProvider>
      <SidebarProvider className="h-svh !min-h-0">
        <SidebarAutoCollapse />
        <AppSidebar />
        <main className="relative flex min-w-0 flex-1 flex-col lg:mr-2 lg:mb-2 lg:peer-data-[state=collapsed]:ml-1">
          <div
            ref={contentRef}
            className="flex flex-1 flex-col min-h-0"
            style={{
              opacity: contentVisible ? 1 : 0,
              transition: shouldFadeIn.current ? 'opacity 0.4s linear' : undefined,
            }}
          >
            <div
              ref={toolbarRef}
              className="z-10 flex shrink-0 items-center gap-2 px-3 py-2 lg:px-0 [transform:translateZ(0)]"
            >
              <button
                type="button"
                onClick={() => navigate(ROUTE.ROOT)}
                aria-label="Go home"
                className="lg:hidden flex cursor-pointer items-center border-0 bg-transparent p-0 outline-none"
              >
                <Logo variant="word" size="14" />
              </button>
              <div className="flex items-center gap-2 ml-auto">
                {currentPlatform && (
                  <>
                    <FadeIn>
                      <EventsButton />
                    </FadeIn>
                    <FadeIn>
                      <ModeSelector />
                    </FadeIn>
                  </>
                )}

                <FadeIn show={!!currentUser}>
                  <UserDropdown />
                </FadeIn>
              </div>
            </div>

            <BreadcrumbTitleProvider>
              <div
                data-slot="page-frame"
                className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-none lg:rounded-lg bg-background shadow-card-100-b lg:shadow-card-100"
              >
                <PageFrameHeader scrollY={scrollY} />
                <div
                  ref={wrapperRef}
                  className="relative flex flex-1 flex-col min-h-0 overflow-auto overscroll-none [-webkit-overflow-scrolling:touch]"
                >
                  <div className="flex w-full flex-col pt-px">
                    <ErrorBoundary resetOn={`${location.pathname}${location.search}`}>{children}</ErrorBoundary>
                  </div>
                </div>
              </div>
            </BreadcrumbTitleProvider>
            <NotificationList
              display="global"
              size="large"
              className="absolute bottom-2 left-4 !z-(--z-notifications) !m-0 w-[400px] p-0 [&>*]:mt-2 [&>*]:first:mt-0"
            />
          </div>
        </main>

        {currentPlatform && helpSidebars}
        <AppsRedirectModal />
      </SidebarProvider>
    </PageFrameProvider>
  );
};
